/* eslint-disable */
"use client";

import { useRouter } from "./router";
import { useEffect, useState, useTransition } from "react";
import {
  UserPlus,
  Search,
  PencilLine,
  UsersRound,
  Loader2,
  Coins,
  CheckCircle2,
  UserX,
  UserCheck,
  X,
  Phone,
  Mail,
  ChevronRight,
  BadgeCheck,
  User,
  Wallet,
  Landmark,
  ShieldCheck,
  HeartPulse,
  Building2,
} from "lucide-react";
import type { employees } from "./types";
import {
  Button,
  Card,
  EmptyState,
  EmployeeTypeBadge,
  Field,
  Modal,
  Pill,
  Select,
  StatusPill,
  Switch,
  TextInput,
} from "./ui";
import { PageHeader } from "./sidebar";
import { useUrlFilters } from "./router";
import {
  createEmployee,
  toggleEmployeeStatus,
  updateEmployee,
  type EmployeeInput,
} from "./api";
import { cn, formatDate, formatPeso } from "./utils";
import { RollingNumber } from "./rolling-number";

type EmployeeRow = typeof employees.$inferSelect;

type HrisEmployee = {
  id: number | string;
  id_str?: string;
  fullName: string;
  email?: string;
  branch?: string;
  position?: string;
  department?: string;
  code?: string;
  isActive?: boolean;
};

const emptyForm: EmployeeInput = {
  fullName: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  position: "",
  department: "",
  employeeType: "313",
  dailyRate: "",
  hourlyRate: "",
  minutesRate: "",
  hiredAt: "",
  taxExempt: false,
  tinNumber: "",
  sssNumber: "",
  philhealthNumber: "",
  pagibigNumber: "",
  allowanceMf: "",
  allowanceGas: "",
  allowanceLoad: "",
  allowanceHousing: "",
  govHdmf: true,
  govHdmfNumber: "",
  govSss: true,
  govPhilhealth: true,
  govTax: true,
};

function formatIdNumber(value: string, key: "tinNumber" | "sssNumber" | "philhealthNumber" | "pagibigNumber") {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  if (key === "tinNumber") {
    const p3 = digits.slice(0, 3);
    const p2a = digits.slice(3, 6);
    const p2b = digits.slice(6, 9);
    const p4 = digits.slice(9, 13);
    const parts: string[] = [];
    if (p3) parts.push(p3);
    if (p2a) parts.push(p2a);
    if (p2b) parts.push(p2b);
    if (p4) parts.push(p4);
    return parts.join("-");
  }
  if (key === "sssNumber") {
    const p2 = digits.slice(0, 2);
    const p7 = digits.slice(2, 9);
    const p1 = digits.slice(9, 10);
    const parts: string[] = [];
    if (p2) parts.push(p2);
    if (p7) parts.push(p7);
    if (p1) parts.push(p1);
    return parts.join("-");
  }
  if (key === "philhealthNumber") {
    const p2 = digits.slice(0, 2);
    const p9 = digits.slice(2, 11);
    const p1 = digits.slice(11, 12);
    const parts: string[] = [];
    if (p2) parts.push(p2);
    if (p9) parts.push(p9);
    if (p1) parts.push(p1);
    return parts.join("-");
  }
  const p4 = digits.slice(0, 4);
  const p6 = digits.slice(4, 10);
  const p2 = digits.slice(10, 12);
  const parts: string[] = [];
  if (p4) parts.push(p4);
  if (p6) parts.push(p6);
  if (p2) parts.push(p2);
  return parts.join("-");
}
function Avatar({ row }: { row: EmployeeRow }) {
  const alt = row.id % 2 === 0;
  return (
    <span
      className={cn(
        "flex h-9.5 w-9.5 shrink-0 items-center justify-center rounded-xl text-xs font-bold",
        alt
          ? "bg-[color-mix(in_oklab,var(--accent)_18%,transparent)] text-accent-strong"
          : "bg-[color-mix(in_oklab,var(--primary)_16%,transparent)] text-primary-strong"
      )}
    >
      {(row.firstName[0] ?? "") + (row.lastName[0] ?? "")}
    </span>
  );
}

function HrisEmployeePickerPanel({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (emp: HrisEmployee) => void;
}) {
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState<HrisEmployee[]>([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    setLoading(true);
    setErr("");
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL || "";
        const r = await fetch(`${base}/api/clockora/employees?pageSize=1000`, {
          headers: { "Content-Type": "application/json" },
        });
        const data = await r.json();
        const rows: HrisEmployee[] =
          Array.isArray(data?.employees)
            ? data.employees
            : Array.isArray(data)
            ? data
            : [];
        setList(
          rows
            .filter((e) => Boolean(e && e.fullName))
            .sort((a, b) => {
              const aa = Number(a.isActive !== false) - Number(b.isActive !== false);
              if (aa !== 0) return aa;
              return String(a.fullName || "").localeCompare(String(b.fullName || ""));
            })
        );
      } catch (e: any) {
        setErr(e?.message || "Could not load HRIS employees.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = list.filter((e) => {
    if (!q.trim()) return true;
    // Normalize: strip accents, lowercase, collapse whitespace.
    const normalize = (s: string) =>
      (s || "")
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/\s+/g, " ")
        .trim()
        .toLowerCase();
    const hay = normalize(
      `${e.fullName} ${e.code || ""} ${e.position || ""} ${e.department || ""} ${e.email || ""}`
    );
    const terms = normalize(q).split(" ").filter(Boolean);
    return terms.every((t) => hay.includes(t));
  });

  return (
    <Card className="rise-in w-full h-full flex flex-col overflow-hidden">
      <div className="flex flex-shrink-0 items-start gap-3.5 border-b border-border px-6 py-5 sm:px-7">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[color-mix(in_oklab,var(--accent)_15%,transparent)] text-accent">
          <BadgeCheck className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-bold text-text">Select an employee</h2>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted">
            Pick an existing account from the employee dashboard to enroll into Payora payroll.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface2 hover:text-text"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-7 sm:py-7">
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" />
            <TextInput
              className="pl-9"
              placeholder="Search by name, code, position, or department\u2026"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        <div
          className={cn(
            "h-full overflow-y-auto -mr-2 pr-2 divide-y divide-border/70 rounded-xl border bg-surface2/40",
            list.length === 0 || loading ? "divide-y-0 border-0 bg-transparent" : ""
          )}
        >
          {loading ? (
            <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border/70 bg-surface2/30 text-faint">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
              <p className="text-xs font-semibold">Loading employee dashboard roster&hellip;</p>
            </div>
          ) : err ? (
            <EmptyState
              icon={<X className="h-5 w-5" />}
              title="Could not load employees"
              description={err}
            />
          ) : list.length === 0 ? (
            <EmptyState
              icon={<UsersRound className="h-6 w-6" />}
              title="No HRIS employees yet"
              description="Create employees in the employee dashboard first, then return here to enroll them into payroll."
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Search className="h-5 w-5" />}
              title="No matches"
              description="Try a different name or department."
            />
          ) : (
            filtered.map((e) => (
              <button
                key={String(e.id)}
                type="button"
                onClick={() => onPick(e)}
                className="group flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface3/70 first:rounded-t-xl last:rounded-b-xl"
              >
                <span
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-[12px] font-bold"
                  style={{
                    background:
                      "color-mix(in oklab, var(--primary) 14%, transparent)",
                    color: "var(--primary-strong)",
                  }}
                >
                  {(e.fullName || "??").trim().split(/\s+/).filter(Boolean).slice(0, 2).map((s: string) => s[0]).join("").toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-text">
                    {e.fullName}
                    {e.isActive === false && (
                      <span className="ml-2 inline-flex">
                        <Pill tone="muted">Inactive in HRIS</Pill>
                      </span>
                    )}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-faint">
                    {e.code && <span className="font-mono font-semibold">{e.code}</span>}
                    {e.position && <span>{e.position}</span>}
                    {e.department && <span>&#183; {e.department}</span>}
                    {e.branch && <span>&#183; {e.branch}</span>}
                  </div>
                  {e.email && (
                    <p className="mt-0.5 truncate text-[11px] text-muted">{e.email}</p>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-faint transition-transform group-hover:translate-x-0.5 group-hover:text-primary-strong" />
              </button>
            ))
          )}
        </div>
      </div>

      <div className="flex flex-shrink-0 items-center justify-between gap-2.5 border-t border-border px-6 py-4 sm:px-7">
        <p className="text-[11px] font-semibold text-faint">
          {list.length > 0 && `${filtered.length} of ${list.length} employee${list.length === 1 ? "" : "s"}`}
        </p>
        <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
      </div>
    </Card>
  );
}

function EmployeeFormPanel({
  mode,
  employee,
  departments,
  onClose,
  prefill,
}: {
  mode: "enroll" | "edit";
  employee: EmployeeRow | null;
  departments: string[];
  onClose: () => void;
  prefill?: { fullName?: string; email?: string; position?: string; department?: string; phone?: string };
}) {
  const router = useRouter();
  const WORKING_DAYS_BY_TYPE: Record<string, number> = { "313": 313, "261": 261 };
  const round2 = (n: number) => Math.round(n * 100) / 100;

  const deriveInitial = (): { input: EmployeeInput; monthlySalary: string; activeTab: "info" | "salary" } => {
    if (employee) {
      const fullName = `${employee.firstName || ""} ${employee.lastName || ""}`.trim();
      const type = employee.employeeType;
      const dailyNum = Number(employee.dailyRate) || 0;
      const days = WORKING_DAYS_BY_TYPE[type] || 313;
      const anyEmp = employee as EmployeeRow & Record<string, any>;
      // Use the stored monthly salary directly to avoid round-trip drift;
      // fall back to computing it from the daily rate for legacy records.
      const storedMonthly = anyEmp.monthlySalary ? String(anyEmp.monthlySalary) : "";
      const monthlyNum = storedMonthly
        ? Number(storedMonthly) || 0
        : dailyNum && days ? round2((dailyNum * days) / 12) : 0;
      return {
        input: {
          fullName,
          firstName: employee.firstName,
          lastName: employee.lastName,
          email: employee.email,
          phone: employee.phone,
          position: employee.position,
          department: employee.department,
          employeeType: type,
          monthlySalary: storedMonthly || (monthlyNum ? String(monthlyNum) : ""),
          dailyRate: String(dailyNum || ""),
          hourlyRate: anyEmp.hourlyRate ?? "",
          minutesRate: anyEmp.minutesRate ?? "",
          hiredAt: employee.hiredAt ?? "",
          taxExempt: employee.taxExempt,
          tinNumber: anyEmp.tinNumber ?? "",
          sssNumber: anyEmp.sssNumber ?? "",
          philhealthNumber: anyEmp.philhealthNumber ?? "",
          pagibigNumber: anyEmp.pagibigNumber ?? "",
          allowanceMf: anyEmp.allowanceMf ?? "",
          allowanceGas: anyEmp.allowanceGas ?? "",
          allowanceLoad: anyEmp.allowanceLoad ?? "",
          allowanceHousing: anyEmp.allowanceHousing ?? "",
          govHdmf: anyEmp.govHdmf ?? true,
          govHdmfNumber: anyEmp.govHdmfNumber ?? "",
          govSss: anyEmp.govSss ?? true,
          govPhilhealth: anyEmp.govPhilhealth ?? true,
          govTax: anyEmp.govTax ?? true,
        },
        monthlySalary: storedMonthly || (monthlyNum ? String(monthlyNum) : ""),
        activeTab: "info",
      };
    }
    if (prefill) {
      const full = (prefill.fullName || "").trim();
      const parts = full.split(/\s+/);
      const firstName = parts[0] || "";
      const lastName = parts.slice(1).join(" ") || "";
      return {
        input: {
          ...emptyForm,
          fullName: full,
          firstName,
          lastName,
          email: prefill.email || "",
          phone: prefill.phone || "",
          position: prefill.position || "",
          department: prefill.department || "",
        },
        monthlySalary: "",
        activeTab: "info",
      };
    }
    return { input: { ...emptyForm }, monthlySalary: "", activeTab: "info" };
  };

  const derived = deriveInitial();
  const [form, setForm] = useState<EmployeeInput>(derived.input);
  const [monthlySalary, setMonthlySalary] = useState<string>(derived.monthlySalary);
  const [activeTab, setActiveTab] = useState<"info" | "salary">(derived.activeTab);
  const [error, setError] = useState("");
  const [pending, startTransition] = useTransition();

  const set = <K extends keyof EmployeeInput>(key: K, value: EmployeeInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const days = WORKING_DAYS_BY_TYPE[form.employeeType] || 313;
  const monthlyNum = Number(monthlySalary) || 0;
  const dailyNum = monthlyNum && days ? round2((monthlyNum * 12) / days) : 0;
  const hourlyNum = dailyNum ? round2(dailyNum / 8) : 0;
  const minutesNum = hourlyNum ? round2(hourlyNum / 60) : 0;

  const syncDailyFromMonthly = (monthlyStr: string) => {
    setMonthlySalary(monthlyStr);
    const mNum = Number(monthlyStr) || 0;
    const d = WORKING_DAYS_BY_TYPE[form.employeeType] || 313;
    const dailyStr = mNum && d ? String(round2((mNum * 12) / d)) : "";
    setForm((f) => ({ ...f, dailyRate: dailyStr }));
  };

  const submit = () => {
    setError("");
    const fullName = (form.fullName || `${form.firstName || ""} ${form.lastName || ""}`).trim();
    const parts = fullName.split(/\s+/);
    const firstName = parts[0] || "";
    const lastName = parts.slice(1).join(" ") || "";
    const finalDaily = form.dailyRate || (monthlyNum ? String(dailyNum) : "");
    const payload: EmployeeInput = {
      ...form,
      fullName,
      firstName,
      lastName,
      monthlySalary: monthlySalary.trim(),
      dailyRate: finalDaily,
      hourlyRate: hourlyNum ? String(hourlyNum) : "",
      minutesRate: minutesNum ? String(minutesNum) : "",
    };
    startTransition(() => {
      void (async () => {
        const res =
          mode === "enroll" ? await createEmployee(payload) : await updateEmployee(employee!.id, payload);
        if (!res.ok) {
          setError(res.error);
          return;
        }
        router.refresh();
        onClose();
      })();
    });
  };

  const onFullNameChange = (v: string) => {
    setForm((f) => ({
      ...f,
      fullName: v,
    }));
  };

  const onEmployeeTypeChange = (t: "313" | "261") => {
    const mNum = Number(monthlySalary) || 0;
    const d = WORKING_DAYS_BY_TYPE[t] || 313;
    const dailyStr = mNum && d ? String(round2((mNum * 12) / d)) : "";
    setForm((f) => ({ ...f, employeeType: t, dailyRate: dailyStr }));
  };

  const displayName =
    mode === "edit"
      ? `${employee?.firstName ?? ""} ${employee?.lastName ?? ""}`.trim()
      : (prefill?.fullName || "").trim();

  return (
    <Card className="rise-in w-full h-full flex flex-col overflow-hidden">
      <div className="flex flex-shrink-0 items-start gap-3.5 border-b border-border px-6 py-5 sm:px-7">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[color-mix(in_oklab,var(--accent)_15%,transparent)] text-accent">
          <UserPlus className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-xl font-bold text-text">
            {mode === "enroll" ? "Enroll new employee" : `Edit ${displayName || ""}`.trim()}
          </h2>
          <p className="mt-0.5 text-[12.5px] leading-relaxed text-muted">
            {mode === "enroll"
              ? "Finish setting up payroll details for this employee. Employee code is generated automatically."
              : `Employee code ${employee?.employeeCode} stays the same.`}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface2 hover:text-text"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-shrink-0 border-b border-border px-6 sm:px-7">
        <div className="-mb-px flex gap-1">
          {(
            [
              { id: "info", label: "Employee's Info", icon: User },
              { id: "salary", label: "Salary Package", icon: Wallet },
            ] as const
          ).map((tab) => {
            const active = activeTab === tab.id;
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "group inline-flex items-center gap-2 cursor-pointer border-b-2 px-4 py-3 text-xs font-bold tracking-wide transition-colors",
                  active
                    ? "border-accent text-accent-strong"
                    : "border-transparent text-faint hover:text-muted"
                )}
              >
                <Icon className={cn("h-4 w-4", active ? "text-accent" : "")} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 sm:px-7 sm:py-7">
        {activeTab === "info" ? (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Full name" className="sm:col-span-2">
              <TextInput
                value={form.fullName || `${form.firstName || ""} ${form.lastName || ""}`.trim()}
                onChange={(e) => onFullNameChange(e.target.value)}
                placeholder="e.g. Maria Santos"
                autoFocus
              />
            </Field>
            <Field label="Email" hint="optional">
              <div className="relative">
                <Mail className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" />
                <TextInput
                  className="pl-9"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="name@company.com"
                />
              </div>
            </Field>
            <Field label="Phone" hint="optional">
              <div className="relative">
                <Phone className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" />
                <TextInput
                  className="pl-9"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  placeholder="09xx xxx xxxx"
                />
              </div>
            </Field>
            <Field label="Position">
              <TextInput value={form.position} onChange={(e) => set("position", e.target.value)} placeholder="e.g. Machine Operator" />
            </Field>
            <Field label="Department" hint="type or pick">
              <TextInput
                value={form.department}
                onChange={(e) => set("department", e.target.value)}
                placeholder="e.g. Operations"
                list="dept-options"
              />
              <datalist id="dept-options">
                {departments.map((d) => (
                  <option key={d} value={d} />
                ))}
              </datalist>
            </Field>
            <Field label="Date hired" hint="optional" className="sm:col-span-2">
              <TextInput type="date" value={form.hiredAt} onChange={(e) => set("hiredAt", e.target.value)} />
            </Field>

            <div className="sm:col-span-2 flex items-center justify-center pt-1">
              <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface2/70 px-3.5 py-1.5">
                <Landmark className="h-3.5 w-3.5 text-primary" />
                <p className="text-[11px] font-black uppercase tracking-[0.18em] text-text">Government ID Numbers</p>
              </span>
            </div>

            {(
              [
                { key: "tinNumber" as const, label: "TIN Number", Icon: Landmark, accent: "primary",
                  placeholder: "000-000-000-0000", maxChars: 18 },
                { key: "sssNumber" as const, label: "SSS Number", Icon: ShieldCheck, accent: "primary",
                  placeholder: "00-0000000-0", maxChars: 12 },
                { key: "philhealthNumber" as const, label: "PhilHealth Number", Icon: HeartPulse, accent: "accent",
                  placeholder: "00-000000000-0", maxChars: 14 },
                { key: "pagibigNumber" as const, label: "Pag-IBIG Number (HDMF)", Icon: Building2, accent: "accent",
                  placeholder: "0000-000000-00", maxChars: 14 },
              ]
            ).map(({ key, label, Icon, accent, placeholder, maxChars }) => (
              <Field key={key} label={label}>
                <div className="relative">
                  <span
                    className="absolute top-1/2 left-3.5 -translate-y-1/2 grid h-7 w-7 shrink-0 place-items-center rounded-md border"
                    style={{
                      borderColor: accent === "accent" ? "color-mix(in oklab, var(--accent) 55%, transparent)" : "color-mix(in oklab, var(--primary) 55%, transparent)",
                      background: accent === "accent" ? "color-mix(in oklab, var(--accent) 12%, transparent)" : "color-mix(in oklab, var(--primary) 12%, transparent)",
                      color: accent === "accent" ? "var(--accent-strong)" : "var(--primary-strong)",
                    }}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <TextInput
                    className="pl-12 h-11 text-[13.5px] font-mono font-semibold tracking-wider"
                    inputMode="numeric"
                    maxLength={maxChars}
                    placeholder={placeholder}
                    value={formatIdNumber(form[key] || "", key)}
                    onChange={(e) => {
                      const digits = e.target.value.replace(/\D/g, "");
                      set(key, digits);
                    }}
                  />
                </div>
              </Field>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Field label="Employee type">
              <div className="grid grid-cols-2 gap-2">
                {(["313", "261"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => onEmployeeTypeChange(t)}
                    className={cn(
                      "border h-11 cursor-pointer rounded-xl text-sm font-bold transition-all",
                      form.employeeType === t
                        ? t === "313"
                          ? "border-primary bg-[color-mix(in_oklab,var(--primary)_14%,transparent)] text-primary-strong"
                          : "border-accent bg-[color-mix(in_oklab,var(--accent)_14%,transparent)] text-accent-strong"
                        : "border-border bg-surface2 text-muted"
                    )}
                  >
                    Type {t}
                  </button>
                ))}
              </div>
            </Field>
            <Field label="Monthly salary (\u20b1)" hint="Gross monthly base">
              <div className="flex gap-2.5">
                <div className="relative flex-1">
                  <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-faint">&#8369;</span>
                  <TextInput
                    className="pl-8 h-11 text-[14px]"
                    inputMode="decimal"
                    value={monthlySalary}
                    onChange={(e) => syncDailyFromMonthly(e.target.value)}
                    placeholder="Enter monthly salary"
                  />
                </div>
                <button
                  type="button"
                  aria-pressed={form.taxExempt}
                  onClick={() => set("taxExempt", !form.taxExempt)}
                  className={cn(
                    "inline-flex h-11 shrink-0 cursor-pointer items-center gap-2 rounded-xl border px-5 text-sm font-semibold transition-all active:scale-[0.97]",
                    form.taxExempt
                      ? "border-transparent bg-accent text-on-accent shadow-[0_10px_26px_-12px_var(--accent)]"
                      : "border-[color-mix(in_oklab,var(--accent)_55%,transparent)] bg-[color-mix(in_oklab,var(--accent)_10%,transparent)] text-accent-strong hover:bg-[color-mix(in_oklab,var(--accent)_18%,transparent)]"
                  )}
                  title="Flag as a minimum wage earner \u2014 excluded from tax deduction"
                >
                  {form.taxExempt ? <CheckCircle2 className="h-4 w-4" /> : <Coins className="h-4 w-4" />}
                  Minimum Wage
                </button>
              </div>
            </Field>

            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field
                label="Daily rate (\u20b1)"
                hint={
                  monthlyNum
                    ? `Monthly \u00d7 12 \u00f7 ${form.employeeType} (${form.employeeType === "313" ? "313 working days" : "261 working days"})`
                    : "varies per province"
                }
              >
                <div className="relative">
                  <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-[color-mix(in_oklab,var(--accent)_70%,transparent)]">&#8369;</span>
                  <TextInput
                    className="pl-8 h-11 text-[14px] font-display font-bold text-accent-strong bg-[color-mix(in_oklab,var(--accent)_10%,transparent)] border-[color-mix(in_oklab,var(--accent)_55%,transparent)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]"
                    inputMode="decimal"
                    value={dailyNum ? String(dailyNum) : form.dailyRate || ""}
                    readOnly
                    placeholder="Auto-computed from monthly salary"
                  />
                </div>
              </Field>
              <Field label="Hourly rate (\u20b1)" hint="Daily rate \u00f7 8 hours">
                <div className="relative">
                  <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-[color-mix(in_oklab,var(--accent)_70%,transparent)]">&#8369;</span>
                  <TextInput
                    className="pl-8 h-11 text-[14px] font-display font-bold text-accent-strong bg-[color-mix(in_oklab,var(--accent)_10%,transparent)] border-[color-mix(in_oklab,var(--accent)_55%,transparent)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]"
                    readOnly
                    placeholder="\u2014"
                    value={hourlyNum ? String(hourlyNum) : ""}
                  />
                </div>
              </Field>
              <Field label="Minutes rate (\u20b1)" hint="Hourly rate \u00f7 60 minutes">
                <div className="relative">
                  <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-[color-mix(in_oklab,var(--accent)_70%,transparent)]">&#8369;</span>
                  <TextInput
                    className="pl-8 h-11 text-[14px] font-display font-bold text-accent-strong bg-[color-mix(in_oklab,var(--accent)_10%,transparent)] border-[color-mix(in_oklab,var(--accent)_55%,transparent)] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.04)]"
                    readOnly
                    placeholder="\u2014"
                    value={minutesNum ? String(minutesNum) : ""}
                  />
                </div>
              </Field>
            </div>

            <div className="sm:col-span-2 rounded-2xl border border-border bg-surface2/50 px-5 py-4">
              <p className={cn("text-[11.5px] leading-relaxed", form.taxExempt ? "font-semibold text-accent-strong" : "text-faint")}>
                {form.taxExempt
                  ? "Marked as a minimum wage earner \u2014 this employee will be excluded from tax deduction on every payroll run."
                  : "Rates are computed automatically from monthly salary. Toggling \u201cMinimum Wage\u201d flags the employee as a minimum-wage earner (tax-exempt) \u2014 it never fills an amount for you."}
              </p>
            </div>

            <div className="sm:col-span-2 flex items-center gap-3 pt-1">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" aria-hidden />
            </div>

            <div className="sm:col-span-2 flex items-center justify-center -mt-2">
              <span className="flex flex-col items-center gap-1.5">
                <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface2/70 px-3.5 py-1.5">
                  <Wallet className="h-3.5 w-3.5 text-accent" />
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-text">Allowances</p>
                </span>
              </span>
            </div>

            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Field label="MF Allowance (\u20b1)" hint="Monthly mobility / fund stipend">
                <div className="relative">
                  <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-faint">&#8369;</span>
                  <TextInput
                    className="pl-8 h-11 text-[14px]"
                    inputMode="decimal"
                    value={form.allowanceMf}
                    onChange={(e) => set("allowanceMf", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </Field>
              <Field label="Gas Allowance (\u20b1)" hint="Fuel or transport allowance">
                <div className="relative">
                  <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-faint">&#8369;</span>
                  <TextInput
                    className="pl-8 h-11 text-[14px]"
                    inputMode="decimal"
                    value={form.allowanceGas}
                    onChange={(e) => set("allowanceGas", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </Field>
              <Field label="Load Allowance (\u20b1)" hint="Mobile / internet load">
                <div className="relative">
                  <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-faint">&#8369;</span>
                  <TextInput
                    className="pl-8 h-11 text-[14px]"
                    inputMode="decimal"
                    value={form.allowanceLoad}
                    onChange={(e) => set("allowanceLoad", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </Field>
              <Field label="Housing Allowance (\u20b1)" hint="Rent / residential stipend">
                <div className="relative">
                  <span className="absolute top-1/2 left-3.5 -translate-y-1/2 text-sm font-semibold text-faint">&#8369;</span>
                  <TextInput
                    className="pl-8 h-11 text-[14px]"
                    inputMode="decimal"
                    value={form.allowanceHousing}
                    onChange={(e) => set("allowanceHousing", e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </Field>
            </div>

            <div className="sm:col-span-2 flex items-center gap-3 pt-1">
              <span className="h-px flex-1 bg-gradient-to-r from-transparent via-border to-transparent" aria-hidden />
            </div>

            <div className="sm:col-span-2 flex items-center justify-center -mt-2">
              <span className="flex flex-col items-center gap-1.5">
                <span className="inline-flex items-center gap-2 rounded-full border border-border bg-surface2/70 px-3.5 py-1.5">
                  <Landmark className="h-3.5 w-3.5 text-primary" />
                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-text">Government Deduction</p>
                </span>
              </span>
            </div>

            <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(
                [
                  { key: "govHdmf" as const, label: "HDMF (Pag-IBIG)", Icon: Building2, accent: "primary",
                    numberField: "govHdmfNumber" as const, numberHint: "Additional amount", numberPlaceholder: "0.00" },
                  { key: "govSss" as const, label: "SSS", Icon: ShieldCheck, accent: "primary" },
                  { key: "govPhilhealth" as const, label: "PhilHealth", Icon: HeartPulse, accent: "accent" },
                  { key: "govTax" as const, label: "Tax", Icon: Landmark, accent: "accent" },
                ]
              ).map(({ key, label, Icon, accent, numberField, numberHint, numberPlaceholder }) => (
                <div
                  key={key}
                  className="group rounded-xl border border-border bg-surface2/50 px-4 py-3.5 transition-colors hover:bg-surface3/70"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <span
                        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border"
                        style={{
                          borderColor: accent === "accent" ? "color-mix(in oklab, var(--accent) 55%, transparent)" : "color-mix(in oklab, var(--primary) 55%, transparent)",
                          background: accent === "accent" ? "color-mix(in oklab, var(--accent) 12%, transparent)" : "color-mix(in oklab, var(--primary) 12%, transparent)",
                          color: accent === "accent" ? "var(--accent-strong)" : "var(--primary-strong)",
                        }}
                      >
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <p className="truncate text-[13.5px] font-bold text-text">{label}</p>
                        {numberField && (
                          <div className="min-w-0 sm:flex-1 max-w-[140px]">
                            <TextInput
                              className="h-9 text-[12.5px] font-mono font-bold px-3 py-0"
                              inputMode="decimal"
                              placeholder={numberPlaceholder || ""}
                              title={numberHint}
                              value={form[numberField] || ""}
                              onChange={(e) => {
                                const nextValue = e.target.value.replace(/[^\d.]/g, "");
                                const parts = nextValue.split(".");
                                const sanitized = parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : parts[0];
                                set(numberField, sanitized);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <Switch checked={form[key]} onChange={(v) => set(key, v)} />
                  </div>
                  <div className="mt-2 pl-12">
                    <p className="truncate text-[11px] text-faint">
                      {numberHint
                        ? `${numberHint} \u00b7 `
                        : ""}
                      {form[key]
                        ? "Deduction enabled on every payroll run"
                        : "Skipped \u2014 employee will not be deducted"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <p className="mt-5 rounded-xl border border-[color-mix(in_oklab,var(--danger)_35%,transparent)] bg-[color-mix(in_oklab,var(--danger)_10%,transparent)] px-4 py-3 text-xs font-semibold text-danger">
            {error}
          </p>
        )}
      </div>

      <div className="flex flex-shrink-0 items-center justify-end gap-2.5 border-t border-border px-6 py-4 sm:px-7">
        <Button variant="ghost" onClick={onClose} disabled={pending}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={pending}>
          {pending && <Loader2 className="h-4 w-4 animate-spin" />}
          {mode === "enroll" ? "Enroll employee" : "Save changes"}
        </Button>
      </div>
    </Card>
  );
}

export function EmployeesClient({
  rows,
  departments,
  stats,
  initialFilters,
}: {
  rows: EmployeeRow[];
  departments: string[];
  stats: { total: number; t313: number; t261: number; active: number };
  initialFilters: { q: string; type: string; status: string; dept: string };
}) {
  const router = useRouter();
  const { values, set, reset } = useUrlFilters(initialFilters);
  const [view, setView] = useState<
    | { kind: "browse" }
    | { kind: "picker" }
    | { kind: "enroll"; prefill: { fullName?: string; email?: string; position?: string; department?: string; phone?: string } }
    | { kind: "edit"; employee: EmployeeRow }
  >({ kind: "browse" });
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [, startTransition] = useTransition();

  const hasFilters = values.q || values.type || values.status || values.dept;

  const toggleStatus = (id: number) => {
    setTogglingId(id);
    startTransition(() => {
      void (async () => {
        await toggleEmployeeStatus(id);
        setTogglingId(null);
        router.refresh();
      })();
    });
  };

  const handlePickHris = (e: HrisEmployee) => {
    setView({
      kind: "enroll",
      prefill: {
        fullName: e.fullName,
        email: e.email,
        position: e.position,
        department: e.department,
        phone: undefined,
      },
    });
  };

  if (view.kind === "picker") {
    return (
      <div className="rise-in flex w-full h-full min-h-0 flex-col">
        <HrisEmployeePickerPanel
          onClose={() => setView({ kind: "browse" })}
          onPick={handlePickHris}
        />
      </div>
    );
  }

  if (view.kind === "enroll" || view.kind === "edit") {
    return (
      <div className="rise-in flex w-full h-full min-h-0 flex-col">
        <EmployeeFormPanel
          mode={view.kind}
          employee={"employee" in view ? view.employee : null}
          departments={departments}
          prefill={"prefill" in view ? view.prefill : undefined}
          onClose={() => setView({ kind: "browse" })}
        />
      </div>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="Workforce"
        title="Employees"
        description="Enroll team members, manage engagement types 313 and 261, and keep daily rates up to date."
        actions={
          <Button variant="accent" onClick={() => setView({ kind: "picker" })}>
            <UserPlus className="h-4 w-4" /> Enroll employee
          </Button>
        }
      />

      {/* filter bar */}
      <Card className="rise-in mb-4 p-3.5">
        <div className="grid grid-cols-2 gap-2.5 md:grid-cols-[1fr_repeat(3,160px)_auto]">
          <div className="relative col-span-2 md:col-span-1">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-faint" />
            <TextInput
              className="pl-9"
              placeholder="Search name, code, or position\u2026"
              value={values.q}
              onChange={(e) => set("q", e.target.value)}
            />
          </div>
          <Select value={values.type} onChange={(e) => set("type", e.target.value)}>
            <option value="">All types</option>
            <option value="313">Type 313</option>
            <option value="261">Type 261</option>
          </Select>
          <Select value={values.status} onChange={(e) => set("status", e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
          <Select value={values.dept} onChange={(e) => set("dept", e.target.value)}>
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Select>
          {hasFilters ? (
            <Button variant="ghost" size="md" onClick={reset}>
              <X className="h-4 w-4" /> Clear
            </Button>
          ) : (
            <div className="hidden items-center px-2 text-[11px] font-semibold text-faint md:flex">
              {rows.length} shown
            </div>
          )}
        </div>
      </Card>

      <Card className="rise-in overflow-hidden">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 border-b border-border px-5 py-3.5 text-[11px] font-bold tracking-wide text-muted uppercase sm:px-6">
          <span className="flex items-center gap-1">Total <RollingNumber text={String(stats.total)} /></span>
          <span className="flex items-center gap-1 text-primary-strong">Type 313 &#183; <RollingNumber text={String(stats.t313)} /></span>
          <span className="flex items-center gap-1 text-accent-strong">Type 261 &#183; <RollingNumber text={String(stats.t261)} /></span>
          <span className="flex items-center gap-1 text-success">Active &#183; <RollingNumber text={String(stats.active)} /></span>
        </div>

        {rows.length === 0 ? (
          <EmptyState
            icon={<UsersRound className="h-6 w-6" />}
            title={stats.total === 0 ? "No employees yet" : "No matches found"}
            description={
              stats.total === 0
                ? "Enroll your first employee to start building the payroll roster."
                : "Try adjusting your search or clearing the filters."
            }
            action={
              stats.total === 0 ? (
                <Button variant="accent" onClick={() => setView({ kind: "picker" })}>
                  <UserPlus className="h-4 w-4" /> Enroll employee
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b border-border bg-surface2 text-left text-[10px] font-bold tracking-[0.12em] text-faint uppercase">
                  <th className="px-5 py-3 sm:px-6">Employee</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Position &#183; Department</th>
                  <th className="px-4 py-3 text-right">Daily rate</th>
                  <th className="px-4 py-3">Hired</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="group border-b border-border/60 transition-colors last:border-0 hover:bg-surface2/70">
                    <td className="px-5 py-3.5 sm:px-6">
                      <div className="flex items-center gap-3">
                        <Avatar row={row} />
                        <div>
                          <p className="font-semibold text-text">
                            {row.firstName} {row.lastName}
                          </p>
                          <p className="text-[11px] font-medium text-faint">{row.employeeCode}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3.5"><EmployeeTypeBadge type={row.employeeType} /></td>
                    <td className="px-4 py-3.5">
                      <p className="font-medium text-text">{row.position || "\u2014"}</p>
                      <p className="text-[11px] text-faint">{row.department || "General"}</p>
                    </td>
                    <td className="px-4 py-3.5 text-right font-display font-bold text-text">
                      {formatPeso(Number(row.dailyRate))}
                      {row.taxExempt && (
                        <span className="ml-1.5 align-middle">
                          <Pill tone="yellow">Tax-Exempt</Pill>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3.5 text-xs font-medium whitespace-nowrap text-muted">{formatDate(row.hiredAt)}</td>
                    <td className="px-4 py-3.5"><StatusPill status={row.status} /></td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setView({ kind: "edit", employee: row })}
                          className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-faint transition-colors hover:bg-surface3 hover:text-primary-strong"
                          title="Edit employee"
                        >
                          <PencilLine className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleStatus(row.id)}
                          disabled={togglingId === row.id}
                          className={cn(
                            "inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 text-[11px] font-bold whitespace-nowrap transition-all active:scale-[0.96] disabled:pointer-events-none disabled:opacity-50",
                            row.status === "active"
                              ? "border-[color-mix(in_oklab,var(--danger)_40%,transparent)] text-danger hover:bg-[color-mix(in_oklab,var(--danger)_12%,transparent)]"
                              : "border-[color-mix(in_oklab,var(--success)_40%,transparent)] text-success hover:bg-[color-mix(in_oklab,var(--success)_12%,transparent)]"
                          )}
                          title={
                            row.status === "active"
                              ? "Set status to inactive \u2014 excluded from future payroll runs"
                              : "Set status back to active"
                          }
                        >
                          {togglingId === row.id ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : row.status === "active" ? (
                            <>
                              <UserX className="h-3.5 w-3.5" /> Deactivate
                            </>
                          ) : (
                            <>
                              <UserCheck className="h-3.5 w-3.5" /> Reactivate
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
